import { useSafeTestJsonParser, type JsonParseResult } from "./useTestJsonParser";
import { Dialog, TextareaAutosize } from "@mui/material";
import PCheckbox from "components/PCheckbox";
import { useMemo, useState } from "react";
import { TestCaseLabelsAndDescription } from "./TestCaseLabelsAndDescription";
import { AiResponseValidationReason, ProfileGenTestCaseView } from "generated/graphql";
import useTestCaseManager from "./useTestCaseManager";
import { parseSafeJSON } from "Backend";
import { LoadingButton } from "@mui/lab";

export type EditProfileResponseDialogProps = {
  testCase: ProfileGenTestCaseView;
  open: boolean;
  onClose: () => void;
};

export const EditProfileResponseDialog = ({ testCase, open, onClose }: EditProfileResponseDialogProps) => {
  const [editedTestCase, setEditedTestcase] = useState<ProfileGenTestCaseView>({ ...testCase });
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [validationReasons, setValidationReasons] = useState<AiResponseValidationReason[]>([]);
  const { validateTestCase, updateTestCase } = useTestCaseManager();

  const renderValidationReason = (reason: AiResponseValidationReason) => {
    if (reason.__typename === "ExtraneousCell") {
      return `Cell "${reason.extraneousKey}" is extra`;
    } else if (reason.__typename === "InArray") {
      return `Element ${reason.index}: ${renderValidationReason(reason.inner)}`;
    } else if (reason.__typename === "IncorrectCellType") {
      return `Cell "${reason.key}" does not satisfy type ${reason.correctType}`;
    } else if (reason.__typename === "IsNotMultiResponse") {
      return "Should be multi response";
    } else if (reason.__typename === "IsNotSingleResponse") {
      return "Should be single response";
    } else if (reason.__typename === "MissingCell") {
      return `Cell "${reason.missingKey}" is missing`;
    } else if (reason.__typename === "NotFound") {
      return "Not found";
    } else {
      return JSON.stringify(reason);
    }
  };

  const jsonParser = useSafeTestJsonParser();

  const json: JsonParseResult = useMemo(
    () => jsonParser(editedTestCase.expectedResponse),
    [editedTestCase.expectedResponse, jsonParser]
  );

  const handleValidate = async () => {
    setIsValidating(true);
    setValidationReasons([]);
    try {
      const reasons = await validateTestCase({ ...editedTestCase, expectedResponse: JSON.stringify(json) });
      setValidationReasons(reasons ?? []);
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async () => {
    return updateTestCase({ ...editedTestCase, expectedResponse: JSON.stringify(json) }).then(onClose);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <div className="p-4">
        <header className="sticky z-10 top-0 bg-white py-2">
          <div className="grid gap-2">
            <p className="text-xl font-semibold">Edit Expected Response</p>
          </div>
        </header>
        <div className="flex-grow">
          <TestCaseLabelsAndDescription
            description={editedTestCase.description ?? ""}
            onChange={(description) => setEditedTestcase((current) => ({ ...current, description }))}
          />
          <div className="flex items-center">
            <PCheckbox
              label="Expect Failure"
              onChange={(evt) => setEditedTestcase((current) => ({ ...current, expectFailure: evt.target.checked }))}
              checked={editedTestCase.expectFailure}
            />
            <p className="text-xs">Reverses test outcome, useful for logging desired results</p>
          </div>
          <p className="text-xs">
            Every key in this object can be considered an expected key in the response. To ignore a given cell for this
            test case, just delete it from the JSON below.
          </p>
          <p className="text-xs font-semibold">
            Special keys: &quot;_testValueMinScore&quot;, &quot;_testRawMinScore&quot;
          </p>
          {validationReasons.length > 0 && (
            <div className="mt-4 text-red-500 text-sm">
              <ul>
                {validationReasons.map((reason, idx) => (
                  <li key={idx}>{renderValidationReason(reason)}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-4">
            <TextareaAutosize
              className="w-full border-b-peri-500 h-3 text-sm font-mono"
              maxRows={200}
              minRows={3}
              value={JSON.stringify(parseSafeJSON(editedTestCase.expectedResponse), null, 2)}
              onChange={(evt) => setEditedTestcase((current) => ({ ...current, expectedResponse: evt.target.value }))}
            />
          </div>
        </div>
        <footer className="sticky z-10 bottom-0 bg-white py-2">
          <div className="flex items-center justify-end gap-2">
            <LoadingButton variant="text" disabled={isValidating} loading={isValidating} onClick={onClose}>
              Cancel
            </LoadingButton>
            <LoadingButton variant="text" disabled={isValidating} loading={isValidating} onClick={handleValidate}>
              Validate
            </LoadingButton>
            <LoadingButton variant="contained" disabled={isValidating} loading={isValidating} onClick={handleSubmit}>
              Save
            </LoadingButton>
          </div>
        </footer>
      </div>
    </Dialog>
  );
};

export default EditProfileResponseDialog;
